import React, { useCallback, useState } from 'react';
import { Button, Input, Tooltip, Modal } from 'antd';
import { ApolloError } from '@apollo/client';
import { MinusOutlined, EditOutlined, CheckOutlined, CloseOutlined, PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import { t } from 'i18n';
import {TmsBusinessTypeEnum, TmsOperationTypeEnum} from 'enums/TmsEnum';
import { translateBackendError } from '../utils/errorMessage';
import { isBagNumber } from '../../../../../../utils/BarCodeUtil';

const translateBagScanError = (raw: unknown) =>
    translateBackendError(raw, 'tms.task.drawer.bagNumberValidateFailed');

export interface BagInfoRow {
    key: string;
    bagNumber: string;
    destinationSortingCode: string;
    parcelCount: number;
    isBag: boolean;
    /** 是否已校验通过，校验通过后不允许再次编辑 */
    validated: boolean;
}

interface BagInfoTableProps {
    dataSource: BagInfoRow[];
    onChange?: (data: BagInfoRow[]) => void;
    taskId: number;
    taskWaypointId: number;
    warehouseId: number;
    routeName?: string;
    readOnly?: boolean;
    isEditing?: boolean;
    onEditingStateChange?: (isEditing: boolean) => void;
    /**
     * 进入编辑前的“快照行”映射：key 为 `bagNumber`（包牌/运单号条码），value 为当时的行数据。
     * 用途：用户在编辑态中删除某条记录、又立刻把同一条条码加回来的场景下，
     * 行内勾选校验时如果后端仍判定该条码已存在，前端可直接用快照恢复并跳过 alreadyExists 提示。
     */
    snapshotRowsByBarCode?: Record<string, BagInfoRow>;
    businessType?: TmsBusinessTypeEnum;
    /** 操作类型：Loading=装车（默认，走装车扫描校验）；Unloading=干线卸车（校验包牌须已装在本任务上） */
    operationType?: TmsOperationTypeEnum;
    /**
     * 已移出的行（装车明细已软删）：排在 dataSource 之后，条码划线、整行置灰、无操作按钮；
     * 查看态与编辑态都展示，均不可编辑，不进 onChange。不传即不展示（卸车、转运等调用方）
     */
    removedRows?: BagInfoRow[];
}

interface HandleTmsLoadingScanEventResult {
    success: boolean;
    parcelQuantity: number;
    isBag: boolean;
    message?: string | null;
    destinationSortingCode?: string | null;
}

const HANDLE_TMS_LOADING_SCAN_EVENT = gql`
    mutation HandleTmsLoadingScanEventForPortal($input: HandleTmsLoadingScanEventInput!) {
        handleTmsLoadingScanEventForPortal(input: $input) {
            success
            parcelQuantity
            isBag
            message
            destinationSortingCode
        }
    }
`;

// 干线卸车加包牌校验：包牌须已装在本任务上；返回结果形状与装车扫描一致，前端统一处理
const VALIDATE_TMS_UNLOADING_BAG = gql`
    mutation ValidateTmsUnloadingBag($input: ValidateTmsUnloadingBagInput!) {
        validateTmsUnloadingBag(input: $input) {
            success
            parcelQuantity
            isBag
            message
            destinationSortingCode
        }
    }
`;

const DESTINATION_NOT_IN_ROUTE_KEY = 'errors.business.tms.loadingDetail.destinationNotInRoute';

const BagInfoTable: React.FC<BagInfoTableProps> = ({
    dataSource,
    onChange,
    taskId,
    taskWaypointId,
    warehouseId,
    routeName = '',
    readOnly = false,
    isEditing = false,
    onEditingStateChange,
    snapshotRowsByBarCode,
    businessType,
    operationType = TmsOperationTypeEnum.Loading,
    removedRows = [],
}) => {
    const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
    const [editingValues, setEditingValues] = useState<Record<string, string>>({});
    const [originalValues, setOriginalValues] = useState<Record<string, string>>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const [handleTmsLoadingScanEvent] = useMutation<{
        handleTmsLoadingScanEventForPortal: HandleTmsLoadingScanEventResult;
    }>(HANDLE_TMS_LOADING_SCAN_EVENT);

    const [validateTmsUnloadingBag] = useMutation<{
        validateTmsUnloadingBag: HandleTmsLoadingScanEventResult;
    }>(VALIDATE_TMS_UNLOADING_BAG);

    // 当外层编辑状态变为 false 时，重置内部编辑状态
    React.useEffect(() => {
        if (!isEditing && editingKeys.size > 0) {
            setEditingKeys(new Set());
            setEditingValues({});
            setOriginalValues({});
            setValidationErrors({});
        }
    }, [isEditing, editingKeys]);

    // 监听内部编辑状态变化
    React.useEffect(() => {
        onEditingStateChange?.(editingKeys.size > 0);
    }, [editingKeys, onEditingStateChange]);

    const handleDelete = useCallback(
        (key: string) => {
            onChange?.(dataSource.filter((row) => row.key !== key));
            setEditingKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            setEditingValues((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            setOriginalValues((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        },
        [dataSource, onChange]
    );

    const updateRow = useCallback(
        (key: string, update: Partial<BagInfoRow>) => {
            onChange?.(dataSource.map((item) => (item.key === key ? { ...item, ...update } : item)));
        },
        [dataSource, onChange]
    );

    const handleEdit = (key: string, value: string) => {
        // 校验通过后的记录不允许再次编辑（与 parcelCount 无关，parcelCount 可能为 0）
        const row = dataSource.find((item) => item.key === key);
        if (row && row.validated) {
            return;
        }
        setEditingKeys((prev) => new Set([...prev, key]));
        setEditingValues((prev) => ({ ...prev, [key]: value }));
        setOriginalValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleCancel = (key: string) => {
        handleDelete(key);
    };

    const isTikTokTask = businessType === TmsBusinessTypeEnum.TikTok;
    const bagNumberColumnLabel = isTikTokTask
        ? t('tms.task.drawer.bagTable.bagNumber')
        : t('tms.task.drawer.bagTable.bagNumberOrTrackingNumber');

    /**
     * 处理「目的地不在本线路」异常：弹窗确认后支持强制装车
     * @returns true 表示已处理，调用方无需再处理；false 表示非此类异常
     */
    const notRouteBagOrTrackingNumberForceSubmit = useCallback(
        (err: any, key: string, value: string): boolean => {
            const messageKey =
                err instanceof ApolloError && err.graphQLErrors?.[0]
                    ? (err.graphQLErrors[0].extensions?.messageKey as string)
                    : '';
            if (messageKey !== DESTINATION_NOT_IN_ROUTE_KEY) return false;

            const details = (err instanceof ApolloError && err.graphQLErrors?.[0]?.extensions?.details) as
                | Record<string, string>
                | undefined;
            const destinationSortingCode = details?.sortingCode ?? '-';
            const currentRoute = details?.routeName ?? routeName ?? '-';

            Modal.confirm({
                title: t('tms.task.drawer.destinationNotInRouteTitle'),
                content: (
                    <div className="space-y-2 text-sm">
                        <div>
                            <span className="font-medium">{t('tms.task.drawer.destinationNotInRouteCurrentRoute')}: </span>
                            <span className="font-semibold" style={{ color: '#722ed1' }}>{currentRoute}</span>
                        </div>
                        <div>
                            <span className="font-medium">{t('tms.task.drawer.destinationNotInRouteScanned')}: </span>
                            <span className="font-semibold text-red-600">{value}</span>
                        </div>
                        <div>
                            <span className="font-medium">{t('tms.task.drawer.destinationNotInRouteDestination')}: </span>
                            <span className="font-semibold text-red-600">{destinationSortingCode}</span>
                        </div>
                        <div className="pt-4">{t('tms.task.drawer.destinationNotInRouteMessage')}</div>
                    </div>
                ),
                okText: t('tms.task.drawer.destinationNotInRouteContinue'),
                cancelText: t('tms.task.drawer.destinationNotInRouteCancel'),
                okButtonProps: { style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } },
                cancelButtonProps: { style: { color: '#722ed1', borderColor: '#722ed1' } },
                onCancel: () => {},
                onOk: async () => {
                    try {
                        const { data: retryData } = await handleTmsLoadingScanEvent({
                            variables: {
                                input: {
                                    taskId,
                                    taskWayPointId: taskWaypointId,
                                    warehouseId: warehouseId,
                                    barCode: value,
                                    forceSubmit: true,
                                    onlyValidate: true
                                },
                            },
                        });
                        const retryResult = retryData?.handleTmsLoadingScanEventForPortal ?? null;
                        if (!retryResult || !retryResult.success) {
                            const backendMessage = translateBagScanError(retryResult?.message);
                            setValidationErrors((prev) => ({ ...prev, [key]: backendMessage }));
                            return;
                        }
                        const isBag = retryResult.isBag;
                        const parcelCount = retryResult.parcelQuantity ?? (isBag ? 1 : 0);
                        const destSortingCode = retryResult.destinationSortingCode ?? '';
                        updateRow(key, {
                            bagNumber: value,
                            parcelCount,
                            destinationSortingCode: destSortingCode,
                            isBag,
                            validated: true
                        });
                        setEditingKeys((prev) => {
                            const next = new Set(prev);
                            next.delete(key);
                            return next;
                        });
                        setEditingValues((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                        });
                        setOriginalValues((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                        });
                        setValidationErrors((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                        });
                    } catch (retryErr: any) {
                        const backendMessage = translateBagScanError(retryErr);
                        setValidationErrors((prev) => ({ ...prev, [key]: backendMessage }));
                    }
                }
            });
            return true;
        },
        [
            routeName,
            taskId,
            taskWaypointId,
            warehouseId,
            handleTmsLoadingScanEvent,
            updateRow
        ]
    );

    /**
     * 校验行数据
     * 包括必填校验、重复性校验和其他业务规则校验
     */
    const validateRow = (key: string, value: string): string | null => {
        // 必填校验
        if (!value?.trim()) {
            return String(t('tms.task.drawer.bagNumberRequired'));
        }

        const trimmed = value.trim();
        // 条码类型：BAG 区分
        const isBag = isBagNumber(trimmed);

        // 长度校验：必须 > 15 位
        if (trimmed.length < 16) {
            return String(t(isBag ? 'tms.task.drawer.bagNumberInvalid' : 'tms.task.drawer.trackingNumberInvalid'));
        }

        // 重复性校验：已添加的包牌号/运单号不能重复（含其他行正在编辑中的值）
        const otherBagNumbers = new Set<string>();
        dataSource.forEach((row) => {
            if (row.key === key) return;
            const val = editingKeys.has(row.key)
                ? (editingValues[row.key] || '').trim()
                : (row.bagNumber || '').trim();
            if (val) otherBagNumbers.add(val);
        });
        if (otherBagNumbers.has(trimmed)) {
            return String(t(isBag ? 'tms.task.drawer.bagNumberDuplicate' : 'tms.task.drawer.trackingNumberDuplicate'));
        }

        return null;
    };

    /**
     * 判断内容是否有变动
     */
    const hasChanged = (key: string): boolean => {
        const currentValue = editingValues[key];
        const originalValue = originalValues[key];
        return currentValue !== originalValue;
    };

    /**
     * 退出该行的编辑态（清理 editingKeys/editingValues/originalValues/validationErrors）。
     * 注意：不改动行数据本身，由调用方决定是否需要恢复/更新。
     */
    const exitEditing = (editKey: string) => {
        setEditingKeys((prev) => {
            const newSet = new Set(prev);
            newSet.delete(editKey);
            return newSet;
        });
        setEditingValues((prev) => {
            const newValues = { ...prev };
            delete newValues[editKey];
            return newValues;
        });
        setOriginalValues((prev) => {
            const newValues = { ...prev };
            delete newValues[editKey];
            return newValues;
        });
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[editKey];
            return newErrors;
        });
    };

    /**
     * 使用 snapshot 恢复行数据，并退出编辑态。
     */
    const restoreFromSnapshot = (editKey: string, barCode: string, snap: BagInfoRow) => {
        updateRow(editKey, {
            bagNumber: barCode,
            parcelCount: snap.parcelCount,
            destinationSortingCode: snap.destinationSortingCode,
            isBag: snap.isBag,
            validated: true,
        });
        exitEditing(editKey);
    };

    const handleSave = async (key: string) => {
        const raw = editingValues[key] || '';
        const value = raw.trim();

        // 统一前端校验（必填/长度/重复等），通过后再决定是否需要保存
        const error = validateRow(key, value);
        if (error) {
            setValidationErrors((prev) => ({ ...prev, [key]: error }));
            return;
        }

        // 优先用 snapshot 恢复：删除后又加回同一条码时，不必再调用后端 onlyValidate 校验
        // snapshotRowsByBarCode 只包含进入编辑前已存在的明细
        const snapshotRow = snapshotRowsByBarCode?.[value];
        if (snapshotRow) {
            restoreFromSnapshot(key, value, snapshotRow);
            return;
        }

        // 无变动：校验已通过，直接退出编辑
        if (!hasChanged(key)) {
            exitEditing(key);
            return;
        }

        // 通过前端必填与长度校验后，调用后端进行业务校验：装车走扫描校验，干线卸车校验包牌须已装在本任务上
        let scanResult: HandleTmsLoadingScanEventResult | null = null;
        try {
            if (operationType === TmsOperationTypeEnum.Unloading) {
                const { data } = await validateTmsUnloadingBag({
                    variables: {
                        input: {
                            taskId,
                            taskWaypointId,
                            bagNumber: value
                        },
                    },
                });
                scanResult = data?.validateTmsUnloadingBag ?? null;
            } else {
                const { data } = await handleTmsLoadingScanEvent({
                    variables: {
                        input: {
                            taskId,
                            taskWayPointId: taskWaypointId,
                            warehouseId: warehouseId,
                            barCode: value,
                            forceSubmit: false,
                            onlyValidate: true
                        },
                    },
                });
                scanResult = data?.handleTmsLoadingScanEventForPortal ?? null;
            }
            if (!scanResult || !scanResult.success) {
                const backendMessage = translateBagScanError(scanResult?.message);
                setValidationErrors((prev) => ({ ...prev, [key]: backendMessage }));
                return;
            }
        } catch (err: any) {
            if (notRouteBagOrTrackingNumberForceSubmit(err, key, value)) return;
            const messageKey =
                err instanceof ApolloError && err.graphQLErrors?.[0]
                    ? (err.graphQLErrors[0].extensions?.messageKey as string)
                    : '';
            if (messageKey === 'errors.business.tms.loadingDetail.alreadyExists') {
                const snap = snapshotRowsByBarCode?.[value];
                if (snap) {
                    restoreFromSnapshot(key, value, snap);
                    return;
                }
            }
            const backendMessage = translateBagScanError(err);
            setValidationErrors((prev) => ({ ...prev, [key]: backendMessage }));
            return;
        }

        // 校验通过，保存数据：返回啥显示啥。容器数量统计见 LoadingDetail。parcelQuantity 必填不为空
        const isBag: boolean = scanResult?.isBag === true;
        const parcelCount: number = scanResult.parcelQuantity ?? (isBag ? 1 : 0);
        const destinationSortingCode = scanResult?.destinationSortingCode ?? '';

        updateRow(key, { bagNumber: value, parcelCount, destinationSortingCode, isBag, validated: true });
        setEditingKeys((prev) => {
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
        });
        setEditingValues((prev) => {
            const newValues = { ...prev };
            delete newValues[key];
            return newValues;
        });
        setOriginalValues((prev) => {
            const newValues = { ...prev };
            delete newValues[key];
            return newValues;
        });
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });
    };

    const handleAddRow = useCallback(() => {
        const newKey = `bag-${Date.now()}`;
        const newRow: BagInfoRow = {
            key: newKey,
            bagNumber: '',
            destinationSortingCode: '',
            parcelCount: 0,
            isBag: false,
            validated: false,
        };
        onChange?.([...dataSource, newRow]);
        // 自动进入编辑状态
        setEditingKeys((prev) => new Set([...prev, newKey]));
        setEditingValues((prev) => ({ ...prev, [newKey]: '' }));
        setOriginalValues((prev) => ({ ...prev, [newKey]: '' }));
    }, [dataSource, onChange]);

    const renderRow = (row: BagInfoRow) => {

        console.log('row', row);
        const isEditing = editingKeys.has(row.key);
        const hasError = !!validationErrors[row.key];
        const isLocked = row.validated;

        const bagNumberDisplay = (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', maxWidth: '100%' }}>
                {isEditing ? (
                    <>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <Input
                                status={hasError ? 'error' : ''}
                                style={{ flex: 1, minWidth: 0 }}
                                value={editingValues[row.key] || ''}
                                autoFocus
                                onChange={(e) => {
                                    setEditingValues((prev) => ({ ...prev, [row.key]: e.target.value }));
                                    // 清除该行的错误提示
                                    if (validationErrors[row.key]) {
                                        setValidationErrors((prev) => {
                                            const newErrors = { ...prev };
                                            delete newErrors[row.key];
                                            return newErrors;
                                        });
                                    }
                                }}
                                placeholder={String(t('tms.task.drawer.bagNumberPlaceholder'))}
                                maxLength={50}
                            />
                            {hasError && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#ff4d4f' }}>
                                    <ExclamationCircleOutlined style={{ fontSize: '12px' }} />
                                    <span>{validationErrors[row.key]}</span>
                                </div>
                            )}
                        </div>
                        <Button
                            icon={<CheckOutlined />}
                            size="small"
                            type="text"
                            style={{ padding: '0 4px', minWidth: 'auto', flexShrink: 0 }}
                            onClick={() => handleSave(row.key)}
                        />
                        <Button
                            icon={<CloseOutlined />}
                            size="small"
                            type="text"
                            style={{ padding: '0 4px', minWidth: 'auto', flexShrink: 0 }}
                            onClick={() => handleCancel(row.key)}
                        />
                    </>
                ) : (
                    <>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.bagNumber || '-'}</span>
                        {!readOnly && !isLocked && (
                            <Tooltip title={String(t('tms.task.drawer.bagTable.edit'))} color="#555" mouseEnterDelay={1}>
                                <Button
                                    icon={<EditOutlined />}
                                    size="small"
                                    type="text"
                                    style={{ padding: '0 4px', minWidth: 'auto', flexShrink: 0 }}
                                    onClick={() => handleEdit(row.key, row.bagNumber)}
                                />
                            </Tooltip>
                        )}
                    </>
                )}
            </div>
        );

        const destinationDisplay = (
            <span className="text-gray-700">{row.destinationSortingCode || '-'}</span>
        );

        const parcelCountDisplay = (
            <span className="text-gray-700">{row.parcelCount ?? 0}</span>
        );

        return (
            <tr key={row.key} className="bg-transparent border-b border-gray-300" style={{ height: '32px' }}>
                <td className="px-2 py-1 border-r border-gray-300" style={{ width: '270px', maxWidth: '270px' }}>{bagNumberDisplay}</td>
                <td className="px-2 py-1 border-r border-gray-300">{destinationDisplay}</td>
                <td className="px-2 py-1 border-r border-gray-300">{parcelCountDisplay}</td>
                {!readOnly && (
                    <td className="px-2 py-1">
                        <Tooltip title={String(t('tms.task.drawer.bagTable.delete'))} color="#555" mouseEnterDelay={1}>
                            <Button
                                type="text"
                                size="small"
                                icon={<MinusOutlined />}
                                onClick={() => handleDelete(row.key)}
                                style={{
                                    color: '#666',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ffe6e6';
                                    e.currentTarget.style.color = '#ff4d4f';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#666';
                                }}
                            />
                        </Tooltip>
                    </td>
                )}
            </tr>
        );
    };

    /** 已移出行：条码 line-through、整行灰字、不可编辑；编辑态操作列留空单元格保持列对齐 */
    const renderRemovedRow = (row: BagInfoRow) => (
        <tr key={row.key} className="bg-transparent border-b border-gray-300 text-gray-400" style={{ height: '32px' }}>
            <td className="px-2 py-1 border-r border-gray-300" style={{ width: '270px', maxWidth: '270px' }}>
                <span className="line-through">{row.bagNumber || '-'}</span>
            </td>
            <td className="px-2 py-1 border-r border-gray-300">{row.destinationSortingCode || '-'}</td>
            <td className="px-2 py-1 border-r border-gray-300">{row.parcelCount ?? 0}</td>
            {!readOnly && <td className="px-2 py-1" />}
        </tr>
    );

    return (
        <div className="mb-4" style={{ backgroundColor: 'transparent' }}>
            <div className="overflow-x-auto w-full">
                <table className="min-w-full text-left border border-gray-300">
                    <thead>
                        <tr className="border-b border-gray-300 bg-gray-50" style={{ height: '32px' }}>
                            <th className="px-2 py-1 text-sm font-bold text-black border-r border-gray-300">
                                {String(bagNumberColumnLabel)}
                            </th>
                            <th className="px-2 py-1 text-sm font-bold text-black border-r border-gray-300">
                                {String(t('tms.task.drawer.bagTable.destination'))}
                            </th>
                            <th className="px-2 py-1 text-sm font-bold text-black border-r border-gray-300">
                                {String(t('tms.task.drawer.bagTable.parcelCount'))}
                            </th>
                            {!readOnly && (
                                <th className="px-2 py-1 text-sm font-bold text-black">
                                    {String(t('tms.task.drawer.bagTable.action'))}
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {dataSource.length === 0 && removedRows.length === 0 && !isEditing ? (
                            <tr className="bg-transparent border-b border-gray-300" style={{ height: '32px' }}>
                                <td colSpan={readOnly ? 3 : 4} className="px-2 py-1 text-center text-gray-500">
                                    {String(t('components.input.noData'))}
                                </td>
                            </tr>
                        ) : (
                            <>
                                {dataSource.map((row) => renderRow(row))}
                                {removedRows.map((row) => renderRemovedRow(row))}
                            </>
                        )}
                    </tbody>
                    {!readOnly && (
                        <tfoot>
                            <tr className="bg-transparent border-b border-gray-300" style={{ height: '32px' }}>
                                <td className="px-2 py-1 border-r border-gray-300" style={{ width: '240px', maxWidth: '240px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <Tooltip title={String(t('tms.task.drawer.bagTable.add'))} color="#555" mouseEnterDelay={1}>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                onClick={handleAddRow}
                                                style={{
                                                    color: '#666',
                                                    transition: 'all 0.3s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f3e5ff';
                                                    e.currentTarget.style.color = '#722ed1';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#666';
                                                }}
                                            />
                                        </Tooltip>
                                    </div>
                                </td>
                                <td className="px-2 py-1 border-r border-gray-300" />
                                <td className="px-2 py-1 border-r border-gray-300" />
                                <td className="px-2 py-1" />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default BagInfoTable;
